import { Card, Flex, Skeleton } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams, useRevalidator, useRouteLoaderData } from "react-router";

import { usePluginContext } from "~/contexts/plugin-context";
import type { loader } from "~/root";
import { normalizeUrl } from "~/shared/utils/normalize-url";
import type { PluginInstance } from "~/widgets/generate-nickname-plugin/types";
import type { PreDepBonusPluginProps } from "~/widgets/pre-dep-bonus-plugin/types";

export type Props = {
  closeModal: () => void;
};
export const PreDepBonusPlugin: FC<Props> = ({ closeModal }) => {
  const { lang } = useParams();
  const ref = useRef<HTMLDivElement>(null);
  const hasRequestedRevalidationRef = useRef(false);
  const data = useRouteLoaderData<typeof loader>("root");
  const revalidator = useRevalidator();
  const pluginSettings = data?.pluginIntegrations?.preDepBonus;
  const preDepBonusFrontUrl = pluginSettings?.front_url;
  const preDepBonusApiUrl = normalizeUrl(pluginSettings?.api_url ?? "");
  const { loadScript, isLoading } = usePluginContext();
  const standaloneUrl = preDepBonusFrontUrl
    ? `${preDepBonusFrontUrl}/standalone.iife.js`
    : undefined;
  const loginToken = data?.loginToken;

  useEffect(() => {
    if (hasRequestedRevalidationRef.current) {
      return;
    }

    if (loginToken) {
      return;
    }

    hasRequestedRevalidationRef.current = true;
    revalidator.revalidate();
  }, [loginToken, revalidator]);

  const handleModalClosed = useCallback(async (): Promise<void> => {
    // Вызываем apply при закрытии модального окна
    if (preDepBonusApiUrl && loginToken) {
      try {
        const response = await fetch(`${preDepBonusApiUrl}/bonus/apply`, {
          method: "POST",
          headers: {
            Authorization: loginToken,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          console.error("Failed to apply bonus");
        }
      } catch (error) {
        console.error("Error applying bonus:", error);
      }
    }
    // Делаем редирект после apply
    window.location.href = `/${lang}/ftd`;
    closeModal();
  }, [closeModal, preDepBonusApiUrl, loginToken, lang]);

  const handleError = (e?: unknown): void => {
    console.error("Pre-dep plugin error:", e);
    handleModalClosed();
  };

  const initialParams: PreDepBonusPluginProps | undefined = useMemo(() => {
    if (!preDepBonusFrontUrl || !preDepBonusApiUrl || !loginToken) {
      return undefined;
    }

    return {
      language: lang,
      baseUrl: preDepBonusFrontUrl,
      apiUrl: preDepBonusApiUrl,
      authToken: loginToken,
      onBonusClaimed: handleModalClosed,
      onModalClosed: handleModalClosed,
      onError: handleError,
    };
  }, [
    handleModalClosed,
    lang,
    loginToken,
    preDepBonusApiUrl,
    preDepBonusFrontUrl,
  ]);

  useEffect(() => {
    if (!standaloneUrl || !initialParams) {
      return;
    }

    let instance: undefined | PluginInstance<PreDepBonusPluginProps>;

    const init = async (): Promise<void> => {
      await loadScript(standaloneUrl);

      if (!ref.current) return;

      instance = window?.PreDepBonusService?.mount(ref.current, initialParams);
    };

    void init();

    return () => {
      instance?.unmount?.();
    };
  }, [initialParams, loadScript, standaloneUrl]);

  const shouldRenderSkeleton = isLoading || !loginToken;

  return shouldRenderSkeleton ? (
    <Card
      border={{ radius: "card" }}
      variant="tertiary"
      width="100%"
      height={556}
      d={{ width: 477, height: 623 }}
      style={{ position: "absolute", zIndex: 1 }}
    >
      <Skeleton radius={8} width={"100%"} height={690} />
    </Card>
  ) : (
    <Flex ref={ref} minWidth="320px" minHeight="400px" />
  );
};
